import {logRuntimeEvent} from './telemetry.js'
import {updateRuntimeDebugState} from './debugState.js'

let refreshInFlight = false
let inMemoryReloadState = null

const RELOAD_MARKER_KEY = '__app_hard_reload_state__'
const RELOAD_COOLDOWN_MS = 30_000
const RELOAD_WINDOW_MS = 120_000
const MAX_RELOADS_PER_WINDOW = 2

function readReloadState() {
  if (typeof window === 'undefined') {
    return inMemoryReloadState
  }

  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARKER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return inMemoryReloadState
  }
}

function writeReloadState(state) {
  inMemoryReloadState = state

  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, JSON.stringify(state))
  } catch {
    // ignore storage failures
  }
}

function getReloadAllowance(reason) {
  const now = Date.now()
  const previous = readReloadState()

  if (previous?.lastTriggeredAt && (now - previous.lastTriggeredAt) < RELOAD_COOLDOWN_MS) {
    return {allowed: false, code: 'cooldown_active', previous}
  }

  if (!previous || !previous.windowStartedAt || (now - previous.windowStartedAt) > RELOAD_WINDOW_MS) {
    return {
      allowed: true,
      nextState: {
        windowStartedAt: now,
        count: 1,
        lastTriggeredAt: now,
        lastReason: reason,
      },
    }
  }

  if ((previous.count || 0) >= MAX_RELOADS_PER_WINDOW) {
    return {allowed: false, code: 'window_limit_reached', previous}
  }

  return {
    allowed: true,
    nextState: {
      ...previous,
      count: (previous.count || 0) + 1,
      lastTriggeredAt: now,
      lastReason: reason,
    },
  }
}

async function clearServiceWorkersAndCaches() {
  if (typeof window === 'undefined') return

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map(async (registration) => {
      ;[registration.installing, registration.waiting, registration.active].forEach(worker => {
        if (!worker) return
        try {
          worker.postMessage({type: 'CLEAR_RUNTIME_CACHES'})
        } catch {
          // ignore message delivery errors
        }
      })
      await registration.unregister()
    }))
  }

  if ('caches' in window) {
    const keys = await caches.keys()
    await Promise.all(keys.map(key => caches.delete(key)))
  }
}

async function triggerHardReload({reason = 'manual', clearRuntime = true} = {}) {
  if (typeof window === 'undefined') return false

  if (refreshInFlight) {
    logRuntimeEvent('hard_reload_suppressed', {
      reason,
      code: 'reload_in_flight',
    })
    return false
  }

  const allowance = getReloadAllowance(reason)
  if (!allowance.allowed) {
    updateRuntimeDebugState({
      lastReloadSuppression: {
        reason,
        code: allowance.code,
        at: new Date().toISOString(),
      },
    })
    logRuntimeEvent('hard_reload_suppressed', {
      reason,
      code: allowance.code,
    })
    return false
  }

  refreshInFlight = true
  writeReloadState(allowance.nextState)

  logRuntimeEvent('hard_reload_triggered', {reason, clearRuntime})

  try {
    if (clearRuntime) {
      await clearServiceWorkersAndCaches()
    }
  } finally {
    const url = new URL(window.location.href)
    url.searchParams.set('__app_refresh', Date.now().toString())
    window.location.replace(url.toString())
  }

  return true
}

export async function forceRefreshApp({reason = 'manual'} = {}) {
  return triggerHardReload({reason, clearRuntime: true})
}

export async function hardReloadApp({reason = 'runtime'} = {}) {
  return triggerHardReload({reason, clearRuntime: false})
}
